/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useState } from "react";
import { Card } from "../Components/ui/card";
import { getIssueColumns, columnDisplayNames } from "@/Components/Issues/issues_table/columns";
import { Issue } from "@/Components/Issues/Issue";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/Components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/ui/tooltip";
import { FilePlus2 } from "lucide-react";
import { DataTableWrapper } from "@/Components/datatable/data-table";
import React from "react";
import { AppContext } from "@/App";
import { useLocation } from "react-router-dom";
import { ColumnFiltersState } from "@tanstack/react-table";
import Confirmation from "@/hooks/confirmation";
import { add, formatDate } from "date-fns";
import PageLockProvider, { PageLockContext, PageName } from "@/Components/PageLockProvider";
import HelpLink from "@/Components/ui/help-link";
import CSV_Importer from "@/Components/datatable/data_importer/csv_importer";
import IssueForm from "@/Components/Issues/issues_table/IssueForm";
import IssuePopup from "@/Components/Issues/issues_table/IssuePopup";
import { DeleteIcon, EditIcon } from "@/Components/ui/Icons/TooltipIcon";
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider";
import { getBasicPageTourViewOnlySteps, getBasicTourSteps } from "@/Components/Issues/issues_wizards/basic_page_tour";
import { IssueContext } from "@/Components/Issues/Issue";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";
import TutorialChecker from "@/Components/Tutorial/TutorialChecker";
import { Tutorial } from "@/lib/utils";

export type IssueColumnVisibility = {
    issueId: boolean
    workstream: boolean
    workPackage: boolean
    description: boolean
    resolution: boolean
    rating: boolean
    status: boolean
    date: boolean
    raisedBy: boolean
    owner: boolean
    notes: boolean
    displayOnStatusReport: boolean
    displayOnDashboard: boolean
    seen: boolean
    acknowledged: boolean
}

function IssuesPage() {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);
    const wizard = useContext(WizardContext);
    const zoomLevel = useResponsiveZoom();
    const isMobile = useIsMobile();

    const [demoMode, setDemoMode] = useState<boolean>(false);
    const useInlineEditing = app.applicationUser?.userSettings?.inlineTableEditing ?? false;

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [loading, setLoading] = useState<boolean>(true);

    const locationState = useLocation().state as { passedColumnFilters?: (ColumnFiltersState & { issueId?: number }) | undefined };
    const passedColumnFilters = locationState || undefined;
    const [clearPassedColumnFilters, setClearPassedColumnFilters] = useState<boolean>(false);

    const [dialogTitle, setDialogTitle] = useState("Group Edit Tasks");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    const users = app.teamMembers.concat(app.projectManagers).concat(app.sponsors);

    const [issueTableContent, setIssueTableContent] = useState<Issue[]>([]);
    const [issueTableDemoContent, setIssueTableDemoContent] = useState<Issue[]>([]);

    const [rowSelection, setRowSelection] = useState({});

    const [importedRows, setImportedRows] = useState<any[]>([]);

    React.useEffect(() => {
        if (importedRows.length > 0) {
            const newIssues = importedRows.map((row) => {
                const newIssue: Issue = {
                    ...row,
                    issueId: 0,
                    projectId: app.selectedProject?.id || 0,
                    userId: users.find(u => u.displayName === row.owner)?.id || "",
                    workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === row.workPackage)?.workPackageId || 0,
                    workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                };
                return newIssue;
            });
            saveIssues(newIssues);
            setImportedRows([]);
        }
    }, [importedRows]);

    const importDataType: { name: string, type: any, example: string }[] = [
        { name: "workPackage", "type": String, example: "WP-1" },
        { name: "description", "type": String, example: "Issue description" },
        { name: "resolution", "type": String, example: "Resolution steps" },
        { name: "rating", "type": String, example: "High" },
        { name: "status", "type": String, example: "Open" },
        { name: "date", "type": Date, example: "2026-01-21" },
        { name: "raisedBy", "type": String, example: "John Doe" },
        { name: "owner", "type": String, example: "Jane Smith" },
        { name: "notes", "type": String, example: "Additional notes" },
        { name: "displayOnDashboard", "type": Boolean, example: "true" },
        { name: "displayOnStatusReport", "type": Boolean, example: "true" }
    ];

    React.useEffect(() => {
        if (app.authLevel !== 3 && !lock.viewOnly) { // viewer cant see this page. Sponsor can see it but not edit it
            const down = (e: KeyboardEvent) => {
                if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    //setDialogOpen((open) => !open)
                    addIssue();
                }
            }
            window.addEventListener("keydown", down)
            return () => window.removeEventListener("keydown", down)
        }
        populateDemoData();
    }, []);

    React.useEffect(() => {
        refreshIssues();
    }, [app.selectedProject]);

    // Keep track of previous issue table content for comparison
    const prevIssueTableContentRef = React.useRef<Issue[]>([]);

    React.useEffect(() => {
        // Skip on initial load or when refreshing all Issues
        if (prevIssueTableContentRef.current.length === 0 ||
            loading ||
            issueTableContent.length !== prevIssueTableContentRef.current.length) {
            prevIssueTableContentRef.current = [...issueTableContent];
            return;
        }

        // Find updated Issues by comparing with previous state
        const updatedIssues = issueTableContent.filter((currentIssue) => {
            const prevIssue = prevIssueTableContentRef.current.find(i => i.issueId === currentIssue.issueId);
            if (!prevIssue) return false;

            // Compare all properties to detect changes
            return JSON.stringify(prevIssue) !== JSON.stringify(currentIssue);
        });

        // Save each updated issue to the server
        updatedIssues.forEach(issue => {
            if (issue.issueId > 0) { // Only update existing Issues, not new ones
                updateIssue(issue);
            }
        });

        // Update ref with current state
        prevIssueTableContentRef.current = [...issueTableContent];
    }, [issueTableContent]);

    function showFormDialog(selectedIssue?: Issue) {
        const action = selectedIssue ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedIssue.issueId) {
                    confirmDeleteIssues([selectedIssue.issueId]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
            </div>
        ) : null;

        setDialogTitleBarActions(action);

        setDialogTitle(selectedIssue ? "Edit Issue" : "Create New Issue");

        setDialogContent(
            <IssueForm
                saveIssue={saveIssue}
                issue={selectedIssue}
            />
        );
        setDialogOpen(true);
    }

    function showPopup(selectedIssue: Issue) {
        const actions = (
            <div className="flex flex-row">
                <div onClick={() => {
                    showFormDialog(selectedIssue);
                }}
                    className="ml-2 p-2 rounded-md cursor-pointer hover:bg-blue-600"
                >
                    <EditIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
                <div onClick={(e) => {
                    e.stopPropagation();
                    if (selectedIssue.issueId) {
                        confirmDeleteIssues([selectedIssue.issueId]);
                    }
                }}
                    className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                    <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
            </div>
        );

        setDialogTitle("Issue Details");
        setDialogTitleBarActions(
            app.authLevel !== 3 && !lock.viewOnly ? actions : null
        );
        setDialogContent(
            <IssuePopup
                issue={selectedIssue}
            />
        );
        setDialogOpen(true);
    }

    function refreshIssues() {
        if (app.selectedProject) {
            setLoading(true);
            document.body.style.cursor = 'wait';
            // fetch actions for project
            fetch("Raids/Issues/" + app.selectedProject.id)
                .then((response) => response.json())
                .then((data) => {
                    setIssueTableContent(data);
                    setLoading(false);
                    document.body.style.cursor = 'default';
                }
                ).catch((error) => {
                    app.showActionNotification("Error", "Error fetching issues: " + error.message, "destructive");
                })
                .finally(() => {
                    document.body.style.cursor = 'default';
                });
        }
    }

    function confirmDeleteIssues(issueIds: number[]) {
        setDialogTitleBarActions(null);
        setDialogTitle(issueIds.length > 1 ? "Are you sure you want to delete these issue?" : "Are you sure you want to delete this issue?");
        setDialogContent(<Confirmation onConfirm={() => { deleteIssues(issueIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function editIssue(id: number) {
        if (demoMode) {
            showFormDialog(issueTableDemoContent.find((issue) => issue.issueId === id));
            return;
        }
        showFormDialog(issueTableContent.find((issue) => issue.issueId === id) || undefined);
    }

    function loadColumnVisibility(): IssueColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Issues_columnVisibility");
        if (localStorageValue) {
            const visibiltyValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                issueId: visibiltyValues.find((v) => v.columnId === "issueId")?.isVisible ?? false,
                workstream: visibiltyValues.find((v) => v.columnId === "workstream")?.isVisible ?? false,
                workPackage: visibiltyValues.find((v) => v.columnId === "workPackage")?.isVisible ?? true,
                description: visibiltyValues.find((v) => v.columnId === "description")?.isVisible ?? true,
                resolution: visibiltyValues.find((v) => v.columnId === "resolution")?.isVisible ?? true,
                rating: visibiltyValues.find((v) => v.columnId === "rating")?.isVisible ?? true,
                status: visibiltyValues.find((v) => v.columnId === "status")?.isVisible ?? true,
                date: visibiltyValues.find((v) => v.columnId === "date")?.isVisible ?? true,
                raisedBy: visibiltyValues.find((v) => v.columnId === "raisedBy")?.isVisible ?? false,
                owner: visibiltyValues.find((v) => v.columnId === "owner")?.isVisible ?? true,
                notes: visibiltyValues.find((v) => v.columnId === "notes")?.isVisible ?? false,
                displayOnStatusReport: visibiltyValues.find((v) => v.columnId === "displayOnStatusReport")?.isVisible ?? false,
                displayOnDashboard: visibiltyValues.find((v) => v.columnId === "displayOnDashboard")?.isVisible ?? false,
                seen: visibiltyValues.find((v) => v.columnId === "seen")?.isVisible ?? false,
                acknowledged: visibiltyValues.find((v) => v.columnId === "acknowledged")?.isVisible ?? false,
            })
        }
        return {
            issueId: false,
            workstream: false,
            workPackage: true,
            description: true,
            resolution: true,
            rating: true,
            status: true,
            date: true,
            raisedBy: false,
            owner: true,
            notes: false,
            displayOnStatusReport: false,
            displayOnDashboard: false,
            seen: false,
            acknowledged: false,
        }
    }

    function addIssue() {
        setClearPassedColumnFilters(true);
        if (useInlineEditing) {
            const newIssue: Issue = {
                issueId: 0,
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "",
                workPackage: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageName || "",
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0, // default to first workstream
                workPackageId: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageId || 0, // default to first work package
                description: "",
                resolution: "",
                rating: "Medium",
                status: "Open",
                date: add(new Date(), { days: 1 }),
                raisedBy: "",
                owner: "",
                userId: "",
                notes: "",
                displayOnStatusReport: false,
                displayOnDashboard: false,
                seen: false,
                acknowledged: false,
            };

            saveIssue(newIssue);
        } else {
            showFormDialog();
        }
    }

    function saveIssue(issue: Issue) {
        if (issue.issueId !== 0) {
            updateIssue(issue);
            return;
        }
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newIssue = { ...issue, issueId: Math.floor(Math.random() * 1000000) };
            setIssueTableDemoContent((prev) => [newIssue, ...prev]);
            app.showActionNotification("Success", "Issue Added Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Issue", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(issue),
        })
            .then((response) => response.json())
            .then((data) => {
                setIssueTableContent((prev) => [data, ...prev]);
                app.showActionNotification("Success", "Issue Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding issue: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function saveIssues(issues: Issue[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newIssues = issues.map((issue) => ({ ...issue, issueId: Math.floor(Math.random() * 1000000) }));
            setIssueTableDemoContent((prev) => [...newIssues, ...prev]);
            app.showActionNotification("Success", `${newIssues.length} Issues added from CSV`, "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Issues", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(issues),
        })
            .then((response) => response.json())
            .then((data) => {
                setIssueTableContent((prev) => [...data, ...prev]);
                app.showActionNotification("Success", `${data.length} Issues added from CSV`, "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding issues: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function updateIssue(issue: Issue) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newIssue = { ...issue, issueId: Math.floor(Math.random() * 1000000) };
            setIssueTableDemoContent((prev) =>
                prev.map((i) => (i.issueId === issue.issueId ? newIssue : i))
            );
            app.showActionNotification("Success", "Issue Updated Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch(`/Raids/Issue/${issue.issueId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...issue,
                date: formatDate(issue.date, "yyyy-MM-dd"), // Ensure date is formatted correctly  
            })
        })
            .then((response) => {
                if (response.ok) {
                    if (!useInlineEditing) {
                        // If not using inline editing, update the specific issue in state
                        setIssueTableContent((prev) =>
                            prev.map((i) => (i.issueId === issue.issueId ? issue : i))
                        );
                    }
                    app.showActionNotification("Success", "Issue Updated Successfully!", "success");
                } else {
                    app.showActionNotification("Error", "Error updating issue: " + response.statusText, "destructive");
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating issue: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function deleteIssues(ids: number[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setIssueTableDemoContent(prev => prev.filter((issue) => !ids.includes(issue.issueId)));
            app.showActionNotification("Success", "Issue Deleted", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Issues", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
        })
            .then(() => {
                setIssueTableContent(prev =>
                    prev.filter((issue) => !ids.includes(issue.issueId))
                );
                app.showActionNotification("Success", "Issue Deleted", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error deleting issue: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function getGroupActions() {
        const groupActions = app.authLevel > 3 ? [{
            name: "Delete",
            action: (selectedRows: any[]) => {
                confirmDeleteIssues(selectedRows.map((row) => row.issueId));
            },
        }] : [];

        groupActions.push(
            {
                name: "Set Status to Open",
                action: (selectedRows: any[]) => {
                    const issueStatus = selectedRows.map((row) => ({ "id": row.issueId, "status": "Open" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(issueStatus);
                },
            },
            {
                name: "Set Status to Closed",
                action: (selectedRows: any[]) => {
                    const issueStatus = selectedRows.map((row) => ({ "id": row.issueId, "status": "Closed" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(issueStatus);
                },
            },
            {
                name: "Set Status to Cancelled",
                action: (selectedRows: any[]) => {
                    const issueStatus = selectedRows.map((row) => ({ "id": row.issueId, "status": "Cancelled" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(issueStatus);
                },
            });
        return groupActions;
    }

    function setStatus(issueStatus: { id: number, status: "Closed" | "Cancelled" | "Open" }[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setIssueTableDemoContent(issueTableDemoContent.map((issue) => {
                const updatedIssue = issueStatus.find((is) => is.id === issue.issueId);
                if (updatedIssue) {
                    issue.status = updatedIssue.status;
                }
                return issue;
            }));
            app.showActionNotification("Success", "Status Updated", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/IssueStatus", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(issueStatus),
        })
            .then(() => {
                setIssueTableContent(issueTableContent.map((issue) => {
                    const updatedIssue = issueStatus.find((is) => is.id === issue.issueId);
                    if (updatedIssue) {
                        issue.status = updatedIssue.status;
                    }
                    return issue;
                }));
                app.showActionNotification("Success", "Status Updated", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating status: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function populateDemoData() {
        const demoIssues: Issue[] = [];
        const owners = users.length > 0 ? users.map(u => u.displayName) : ["Alice Johnson", "Bob Smith", "Charlie Brown"];
        const workPackages = app.workPackageOptions?.flatMap(w => w.workPackageNames).map(wp => wp.workPackageName) || ["WP-1", "WP-2", "WP-3"];
        for (let i = 0; i < 20; i++) {
            const randomOwner = owners[Math.floor(Math.random() * owners.length)];
            const randomRaisedBy = owners[Math.floor(Math.random() * owners.length)];
            const randomWorkPackage = workPackages[Math.floor(Math.random() * workPackages.length)];
            const newIssue: Issue = {
                issueId: i,
                projectId: app.selectedProject?.id || 0,
                workstream: `Workstream ${((i - 1) % 3) + 1}`,
                workPackage: randomWorkPackage,
                workstreamId: ((i - 1) % 3) + 1,
                workPackageId: i,
                description: `This is a description for issue ${i}.`,
                resolution: `Resolution steps for issue ${i}.`,
                rating: (i % 3 === 0) ? "High" : (i % 3 === 1) ? "Medium" : "Low",
                status: (i % 4 === 0) ? "Closed" : (i % 4 === 1) ? "Open" : "Cancelled",
                date: add(new Date(), { days: i }),
                raisedBy: randomRaisedBy,
                owner: randomOwner,
                userId: users.find(u => u.displayName === randomOwner)?.id?.toString() || "",
                notes: Math.random() < 0.5 ? `These are some notes for action item number ${i + 1}` : "",
                displayOnDashboard: Math.random() < 0.5,
                displayOnStatusReport: Math.random() < 0.5,
                seen: Math.random() < 0.5,
                acknowledged: Math.random() < 0.5,
            };
            demoIssues.push(newIssue);
        }
        setIssueTableDemoContent(demoIssues);
    }

    const demoButton = (
        <TooltipProvider delayDuration={1}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button id="exitDemoMode" className="rounded-md text-white bg-red-500 h-min px-2 my-auto" onClick={() => setDemoMode(false)}>Exit Demo Mode</button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground">
                        <p>
                            Demo mode allows you to try out the Actions page without making any actual changes to the data.
                        </p>
                        <p>
                            The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Actions.
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const contextValue = {
        dialogOpen,
        setDialogOpen,
        issueTableDemoContent,
        setIssueTableDemoContent,
        showPopup,
        demoMode,
        setDemoMode,
        rowSelection,
        setRowSelection,
        addIssue,
    };

    if (isMobile) {
        return (
            <IssueContext.Provider value={contextValue}>
                <Card className="mb-8 relative" style={{ zoom: zoomLevel }}>
                    {wizard && wizard.getWizardPointer()}
                    <div className="flex flex-row bg-table-issues justify-between px-2 rounded-t-lg">
                        <div className="flex flex-row">
                            <h1 className="text-4xl my-auto p-2">Issues</h1>
                            <div className="my-auto" onClick={refreshIssues}>{lock.getRefreshControl()}</div>
                        </div>
                        <div className="flex flex-row">
                            {app.authLevel !== 3 && !lock.viewOnly ? (
                                <div className="flex justify-end">
                                    <TooltipProvider delayDuration={1}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    id="addNewIssue"
                                                    onClick={() => addIssue()}
                                                    className="rounded-full bg-table-issues border border-white text-table-header-foreground p-2 hover:cursor-pointer m-2"
                                                >
                                                    <FilePlus2 className="w-8 h-8" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="flex flex-col bg-background">
                                                <div className="flex flex-row text-muted-foreground place-content-between">
                                                    <span className="p-2">Create New Issue</span>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            ) : <div></div>}
                        </div>
                        <div className="flex flex-row absolute top-0 right-2 -translate-y-[130%]">{app.authLevel !== 3 && lock.getLockControl()}</div>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent id="issue-dialog" className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }} onInteractOutside={(e) => {
                            if (demoMode) {
                                e.preventDefault();
                            }
                        }}>
                            <DialogHeader>
                                <DialogTitle><div className="flex"><div className="text-4xl my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                    <DataTableWrapper
                        columns={getIssueColumns(
                            (ids: number[]) => confirmDeleteIssues(ids),
                            (id: number) => editIssue(id),
                        )}
                        data={demoMode ? issueTableDemoContent : issueTableContent}
                        setData={demoMode ? setIssueTableDemoContent : setIssueTableContent}
                        loading={loading}
                        viewOnly={app.authLevel === 3 || lock.viewOnly}
                        header_colour="bg-table-issues"
                        table_title="Issues"
                        columnVisibility={loadColumnVisibility()}
                        passedColumnFilters={!clearPassedColumnFilters ? passedColumnFilters as ColumnFiltersState : undefined}
                        groupActions={getGroupActions()}
                        columnDisplayNames={columnDisplayNames}
                        onRowClick={!useInlineEditing ? (row) => showPopup(row.original as Issue) : undefined}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                </Card>
            </IssueContext.Provider>
        );
    }

    return (
        <IssueContext.Provider value={contextValue}>
            <Card className="p-2 m-2 relative" style={{ zoom: zoomLevel }}>
                {wizard && wizard.getWizardPointer()}
                <div className="flex flex-row justify-between">
                    <div className="flex flex-row"><h1 className="text-2xl my-auto p-2">Issues</h1><div className="my-auto" onClick={refreshIssues}>{lock.getRefreshControl()}</div></div>
                    <div className="flex flex-row">{app.authLevel !== 3 && lock.getLockControl()}<HelpLink page="issue" />{demoMode && demoButton}</div>
                    {app.authLevel !== 3 && !lock.viewOnly ? ( // viewer cant see this page. Sponsor can see it but not edit it
                        <div className="flex justify-end">
                            <TooltipProvider delayDuration={1}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            id="addNewIssue"
                                            onClick={() => addIssue()}
                                            className="rounded-full bg-table-issues text-table-header-foreground w-min p-2 hover:cursor-pointer m-2"
                                        >
                                            <FilePlus2 />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="flex flex-col bg-background">
                                        <div className="flex flex-row text-muted-foreground place-content-between">
                                            <span className="p-2">Create New Issue</span>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            {<CSV_Importer propertyTypes={importDataType} setNewRows={setImportedRows} bgColor="bg-table-issues" title="Import Issues from CSV" />}
                        </div>
                    ) : <div></div>}
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent id="issue-dialog" className="flex flex-col max-w-fit" onInteractOutside={(e) => {
                        if (demoMode) {
                            e.preventDefault();
                        }
                    }}
                    >
                        <DialogHeader>
                            <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
                <TutorialChecker
                    tutorialId={Tutorial.RAIDS}
                    tutorial={async () => {
                        setDemoMode(true);
                        const acquiredLock = await lock.lockPage();
                        wizard.setWizardSteps(getBasicPageTourViewOnlySteps({ viewOnly: false, issueContext: contextValue }));
                        wizard.setGuidedTourCompleteFunctionCall(() => () => { lock.unlockPage(acquiredLock?.id); setDemoMode(false); });
                        wizard.startGuidedTour();
                    }} />
                <DataTableWrapper
                    columns={getIssueColumns(
                        (ids: number[]) => confirmDeleteIssues(ids),
                        (id: number) => editIssue(id),
                    )}
                    data={demoMode ? issueTableDemoContent : issueTableContent}
                    setData={demoMode ? setIssueTableDemoContent : setIssueTableContent}
                    loading={loading}
                    viewOnly={app.authLevel === 3 || lock.viewOnly} // viewer can see but not edit
                    header_colour="bg-table-issues"
                    table_title="Issues"
                    columnVisibility={loadColumnVisibility()}
                    passedColumnFilters={!clearPassedColumnFilters ? passedColumnFilters as ColumnFiltersState : undefined}
                    groupActions={getGroupActions()}
                    columnDisplayNames={columnDisplayNames}
                    onRowClick={!useInlineEditing ? (row) => showPopup(row.original as Issue) : undefined}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                />
                <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
            </Card>
        </IssueContext.Provider>
    );
}

function Issues() {
    const app = useContext(AppContext);
    return (
        <PageLockProvider pageName={PageName.Issues} userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} >
            <WizardProvider wizardSteps={getBasicTourSteps()} >
                <IssuesPage />
            </WizardProvider>
        </PageLockProvider>
    );
}

export default Issues;